import { register as registerAccount, registerInvitedCaregiver } from 'api/auth'
import { useAuthContext } from 'components/context/AuthProvider'
import NotificationSys from 'components/NotificationSystem'
import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { routes } from 'routes/routes'
import { ValidatePartnerCodeResponse } from 'types'
import { AuthLogo } from '../AuthLogo/AuthLogo'
import FamilyCardForm from './FamilyCardForm'
import { FamilyPartnerCodeForm } from './FamilyPartnerCodeForm'
import { FamilyRegistrationChoice } from './FamilyRegistrationChoice'
import SignUpFamilyForm from './SignUpFamilyForm'
import {
  FamilyFormType,
  FamilySignupStage,
  InviteFamilyFormType,
  normalizePartnerCode,
  PartnerCodeNotice,
  toFamilyRegisterRequest,
} from './types'

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(base64))
  } catch {
    return null
  }
}

export const AuthPageSignupFamily = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { login } = useAuthContext()
  const inviteToken = searchParams.get('invite')
  const isInvite = !!inviteToken

  // Care-team JWT optionally carries a familyInvitationToken inside its payload.
  // When present, the invited user joins the inviter's Family plan and registration skips payment.
  // Phone-only invites (sent via SMS) carry a phone but no email — the user enters their email on the form.
  const { inviteEmail, isFamilyInvite, isPhoneOnlyInvite } = useMemo(() => {
    if (!inviteToken) {
      return { inviteEmail: undefined, isFamilyInvite: false, isPhoneOnlyInvite: false }
    }
    const payload = decodeJwtPayload(inviteToken)
    const email = (payload?.email as string) || undefined
    return {
      inviteEmail: email,
      isFamilyInvite: !!payload?.familyInvitationToken,
      isPhoneOnlyInvite: !email && !!payload?.phone,
    }
  }, [inviteToken])

  const [formData, setFormData] = useState<FamilyFormType | InviteFamilyFormType | null>(null)
  const [stage, setStage] = useState<FamilySignupStage>(FamilySignupStage.FamilyForm)
  const [familyRegisterLoading, setFamilyRegisterLoading] = useState(false)
  const [fundedRegisterLoading, setFundedRegisterLoading] = useState(false)
  // Unfunded partner code carried into the paid flow for partner attribution.
  const [cardPartnerCode, setCardPartnerCode] = useState<string>()
  const [cardNotice, setCardNotice] = useState<PartnerCodeNotice | null>(null)

  const handleCreate = useCallback(
    async (form: FamilyFormType | InviteFamilyFormType) => {
      if (isFamilyInvite && inviteToken) {
        try {
          setFamilyRegisterLoading(true)
          await registerInvitedCaregiver({
            invitationToken: inviteToken,
            firstname: form.firstName,
            lastname: form.lastName,
            password: form.myNewPassword,
            email: form.email,
          })
          login(form.email, form.myNewPassword, () => navigate(routes.journey))
        } catch (err: unknown) {
          const message =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            'Failed to complete registration. Please try again.'
          NotificationSys.showWarning(message)
        } finally {
          setFamilyRegisterLoading(false)
        }
        return
      }
      setFormData(form)
      // Both organic signup and a care-team invite without a Family seat choose code or card.
      setStage(FamilySignupStage.Choice)
    },
    [isFamilyInvite, inviteToken, login, navigate],
  )

  const goToCard = useCallback((partnerCode?: string, notice: PartnerCodeNotice | null = null) => {
    setCardPartnerCode(partnerCode)
    setCardNotice(notice)
    setStage(FamilySignupStage.CardForm)
  }, [])

  const registerWithFundedCode = useCallback(
    async (partnerCode: string) => {
      if (!formData) return

      setFundedRegisterLoading(true)
      try {
        // The backend activates the partner-funded subscription inside registration — for a
        // care-team invite through the invited endpoint, which also links the care team.
        if (inviteToken) {
          await registerInvitedCaregiver({
            invitationToken: inviteToken,
            firstname: formData.firstName,
            lastname: formData.lastName,
            password: formData.myNewPassword,
            email: formData.email,
            partnerCode: normalizePartnerCode(partnerCode),
          })
        } else {
          await registerAccount(toFamilyRegisterRequest(formData as FamilyFormType, partnerCode))
        }
      } catch {
        // The global error handler has already shown the backend message.
        setFundedRegisterLoading(false)
        return
      }

      login(
        formData.email,
        formData.myNewPassword,
        ({ user }) => {
          if (user.hasActiveSubscription) {
            NotificationSys.showSuccess('Your Partner-Funded Family Subscription is active.')
            navigate(routes.journey)
            return
          }
          // Activation is best effort: the account exists even if the last license was taken
          // between validation and registration, so the user continues with a paid plan.
          NotificationSys.showWarning(
            'Partner-funded access is no longer available. Please choose a paid subscription.',
          )
          navigate(routes.addSubscription)
        },
        () => {
          NotificationSys.showWarning(
            "Can't automatically sign-in after register. Please sign-in manually",
          )
          navigate(routes.signIn)
        },
      )
    },
    [formData, inviteToken, login, navigate],
  )

  const handlePartnerCodeValidated = useCallback(
    (partnerCode: string, result: ValidatePartnerCodeResponse) => {
      switch (result.outcome) {
        case 'funded_available':
          registerWithFundedCode(partnerCode)
          return
        case 'unfunded':
          goToCard(partnerCode, { severity: 'info', message: result.message })
          return
        case 'funded_exhausted':
          // The exhausted code grants nothing and is not attributed — only the message travels on.
          goToCard(undefined, { severity: 'warning', message: result.message })
          return
      }
    },
    [goToCard, registerWithFundedCode],
  )

  return (
    <>
      <AuthLogo />
      {stage === FamilySignupStage.FamilyForm && (
        <SignUpFamilyForm
          handleCreate={handleCreate}
          isInvite={isInvite}
          inviteEmail={inviteEmail}
          isFamilyInvite={isFamilyInvite}
          isPhoneOnlyInvite={isPhoneOnlyInvite}
          submitLoading={familyRegisterLoading}
        />
      )}
      {stage === FamilySignupStage.Choice && (
        <FamilyRegistrationChoice
          onUsePartnerCode={() => setStage(FamilySignupStage.PartnerCode)}
          onPayWithCard={() => goToCard()}
          onCancel={() => navigate(routes.signUp)}
        />
      )}
      {stage === FamilySignupStage.PartnerCode && (
        <FamilyPartnerCodeForm
          onValidated={handlePartnerCodeValidated}
          onBack={() => setStage(FamilySignupStage.Choice)}
          submitting={fundedRegisterLoading}
        />
      )}
      {stage === FamilySignupStage.CardForm && formData && (
        <FamilyCardForm
          formData={formData}
          inviteToken={inviteToken ?? undefined}
          partnerCode={cardPartnerCode}
          notice={cardNotice}
          onCancel={() => setStage(FamilySignupStage.Choice)}
        />
      )}
    </>
  )
}
